import { MutableRefObject, ReactNode, useRef } from 'react';

import Button from '@components/button/Button';
import Icon from '@components/icon/Icon';
import testId from '@constants/testId';
import useOnClickOutside from '@utils/hooks';

interface ModalProps {
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  primaryButtonLabel?: string;
  primaryButtonClick?: () => void;
  primaryButtonDisabled?: boolean;
  primaryButtonLoading?: boolean;
  secondaryButtonLabel?: string;
  secondaryButtonClick?: () => void;
  secondaryButtonDisabled?: boolean;
  isClickOutsideRequired?: boolean;
  primaryButtonStyle?: string;
  secondaryButtonStyle?: string;
  infoText?: ReactNode;
  bodyClassName?: string;
  showCloseButton?: boolean;
  headerClassName?: string;
}

const Modal = ({
  title,
  description,
  onClose,
  children,
  className = '',
  primaryButtonLabel,
  primaryButtonClick,
  primaryButtonDisabled = false,
  primaryButtonLoading = false,
  secondaryButtonLabel,
  secondaryButtonClick,
  secondaryButtonDisabled = false,
  isClickOutsideRequired = true,
  infoText,
  primaryButtonStyle,
  secondaryButtonStyle,
  bodyClassName = '',
  showCloseButton = true,
  headerClassName = ''
}: ModalProps) => {
  const modalRef = useRef() as MutableRefObject<HTMLDivElement>;

  useOnClickOutside(modalRef, (e) => {
    if (isClickOutsideRequired && onClose) {
      e.stopPropagation();
      onClose();
    }
  });

  const hasFooterButtons = primaryButtonLabel || secondaryButtonLabel;

  return (
    <div className="fixed left-0 top-0 z-[60] flex size-full items-center justify-center">
      {/* Modal Overlay */}
      <div className="absolute size-full bg-black opacity-30" />

      {/* Modal Main Content */}
      <div
        className={`relative mx-auto flex max-h-[98%] w-120 flex-col overflow-hidden rounded-lg bg-cofeeNeutral1 ${className}`}
        ref={modalRef}
      >
        {/* Header */}
        <div
          className={`relative border-b border-grey30 px-6 py-4 ${headerClassName}`}
        >
          <div className="pr-10">
            <h2 className="text-xl font-semibold text-content">{title}</h2>
            {description && (
              <p className="mt-1 text-sm text-grey">{description}</p>
            )}
          </div>
          {showCloseButton && (
            <Button
              variant="transparent"
              handleClick={onClose}
              className="absolute right-6 top-4 !size-6 !p-0"
              data-testid={testId.modal.modalClose}
            >
              <Icon name="close" size="24" />
            </Button>
          )}
        </div>
        {/* Body */}
        <div className={`flex-1 overflow-y-auto px-6 py-4 ${bodyClassName}`}>
          {children}
        </div>

        {/* Footer */}
        {hasFooterButtons && (
          <div
            className={`flex items-center border-t border-grey30 px-6 py-4 ${
              infoText ? 'justify-between' : 'justify-end'
            }`}
          >
            {infoText}
            <div className="flex items-center justify-end gap-3 ">
              {secondaryButtonLabel && (
                <Button
                  variant="tertiary"
                  label={secondaryButtonLabel}
                  handleClick={secondaryButtonClick}
                  disabled={secondaryButtonDisabled}
                  className={`!min-w-15 ${secondaryButtonStyle}`}
                />
              )}
              {primaryButtonLabel && (
                <Button
                  variant="primary"
                  label={primaryButtonLabel}
                  handleClick={primaryButtonClick}
                  disabled={primaryButtonDisabled}
                  loading={primaryButtonLoading}
                  className={`!min-w-15 ${primaryButtonStyle}`}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
